import React from 'react';
import dayjs from 'dayjs';

const CalendarGrid = ({ currentDate, events, onDateClick }) => {
  const today = dayjs();
  const startOfMonth = currentDate.startOf('month');
  const endOfMonth = currentDate.endOf('month');
  const startOfCalendar = startOfMonth.startOf('week');
  const endOfCalendar = endOfMonth.endOf('week');

  const days = [];
  let day = startOfCalendar;

  while (day.isBefore(endOfCalendar, 'day') || day.isSame(endOfCalendar, 'day')) {
    days.push(day);
    day = day.add(1, 'day');
  }

  const getEventsForDate = (date) => {
    return events.filter(event => 
      dayjs(event.date).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
    );
  };

  const getEventColors = (eventCount) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-pink-500'
    ];
    return colors.slice(0, Math.min(eventCount, colors.length));
  };

  const hasTimeConflict = (dateEvents) => {
    if (dateEvents.length <= 1) return false;
    
    for (let i = 0; i < dateEvents.length; i++) {
      for (let j = i + 1; j < dateEvents.length; j++) {
        const event1Start = dayjs(`${dateEvents[i].date} ${dateEvents[i].time}`);
        const event1End = event1Start.add(dateEvents[i].duration, 'hour');
        const event2Start = dayjs(`${dateEvents[j].date} ${dateEvents[j].time}`);
        const event2End = event2Start.add(dateEvents[j].duration, 'hour');
        
        if (event1Start.isBefore(event2End) && event2Start.isBefore(event1End)) {
          return true;
        }
      }
    }
    return false;
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-4">
      {/* Week days header */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {weekDays.map((weekDay) => (
          <div key={weekDay} className="text-center font-semibold text-gray-600 py-3">
            {weekDay}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const isCurrentMonth = day.month() === currentDate.month();
          const isToday = day.isSame(today, 'day');
          const dayEvents = getEventsForDate(day);
          const hasConflict = hasTimeConflict(dayEvents);
          const colors = getEventColors(dayEvents.length);
          
          return (
            <div
              key={index}
              onClick={() => onDateClick(day)}
              className={`
                relative min-h-24 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 transform hover:scale-105
                ${isCurrentMonth 
                  ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md' 
                  : 'bg-gray-50 border-gray-100 text-gray-400'
                }
                ${isToday 
                  ? 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50' 
                  : ''
                }
                ${dayEvents.length > 0 ? 'hover:shadow-lg' : ''}
              `}
            >
              <div className={`
                text-sm font-medium mb-1
                ${isToday ? 'text-blue-600 font-bold' : ''}
              `}>
                {day.date()}
              </div>
              
              {/* Event indicators */}
              {dayEvents.length > 0 && (
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event, eventIndex) => (
                    <div
                      key={eventIndex}
                      className={`
                        text-xs px-2 py-1 rounded text-white truncate
                        ${colors[eventIndex] || 'bg-gray-500'}
                        ${hasConflict ? 'animate-pulse' : ''}
                      `}
                      title={`${event.title} - ${event.time}`}
                    >
                      {event.title}
                    </div>
                  ))}
                  
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-500 font-medium">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              )}
              
              {/* Conflict indicator */}
              {hasConflict && (
                <div className="absolute top-1 right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="absolute top-0 right-0 w-3 h-3 bg-red-600 rounded-full" title="Time conflict detected"></div>
                </div>
              )}
              
              {/* Today indicator */}
              {isToday && (
                <div className="absolute bottom-2 right-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;